/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ThemePreferenceActionCreators from '@app/actions/ThemePreferenceActionCreators';
import {getSettingsSubtabs, getSettingsTabs} from '@app/components/modals/utils/SettingsConstants';
import type {GuildRecord} from '@app/records/GuildRecord';
import AccessibilityStore from '@app/stores/AccessibilityStore';
import ChannelStore from '@app/stores/ChannelStore';
import DeveloperModeStore from '@app/stores/DeveloperModeStore';
import FavoritesStore from '@app/stores/FavoritesStore';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import GuildStore from '@app/stores/GuildStore';
import type {
	CandidateSets,
	GroupDMCandidate,
	GuildCandidate,
	QuickActionCandidate,
	SettingsCandidate,
	TextChannelCandidate,
	UserCandidate,
	VirtualGuildCandidate,
	VoiceChannelCandidate,
} from '@app/stores/QuickSwitcherTypes';
import ReadStateStore from '@app/stores/ReadStateStore';
import RelationshipStore from '@app/stores/RelationshipStore';
import SelectedGuildStore from '@app/stores/SelectedGuildStore';
import ThemeStore from '@app/stores/ThemeStore';
import UserSettingsStore from '@app/stores/UserSettingsStore';
import UserStore from '@app/stores/UserStore';
import * as ChannelUtils from '@app/utils/ChannelUtils';
import * as NicknameUtils from '@app/utils/NicknameUtils';
import {FAVORITES_GUILD_ID} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {QuickSwitcherResultTypes} from '@fluxer/constants/src/QuickSwitcherConstants';
import {RelationshipTypes, ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {DAYS_PER_WEEK, MS_PER_DAY} from '@fluxer/date_utils/src/DateConstants';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const UNREAD_SORT_WEIGHT_BOOST = DAYS_PER_WEEK * MS_PER_DAY;

function getChannelRecency(channel: {id: string; lastMessageId: string | null}): number {
	if (channel.lastMessageId) {
		return SnowflakeUtils.extractTimestamp(channel.lastMessageId);
	}
	return SnowflakeUtils.extractTimestamp(channel.id);
}

function getChannelSortWeight(channelId: string, baseWeight: number): number {
	const unreadCount = ReadStateStore.getUnreadCount(channelId);
	const mentionCount = ReadStateStore.getMentionCount(channelId);
	const hasUnread = unreadCount > 0 || mentionCount > 0;
	return hasUnread ? baseWeight + UNREAD_SORT_WEIGHT_BOOST : baseWeight;
}

export function buildCandidateSets(i18n: I18n): CandidateSets {
	const guilds = GuildStore.getGuilds();
	const guildMap = new Map<string, GuildRecord>(guilds.map((guild) => [guild.id, guild]));

	const userCandidates = new Map<string, UserCandidate>();
	const userByChannelId = new Map<string, UserCandidate>();
	const groupDMCandidates: Array<GroupDMCandidate> = [];
	const groupDMByChannelId = new Map<string, GroupDMCandidate>();
	const textChannelCandidates: Array<TextChannelCandidate> = [];
	const voiceChannelCandidates: Array<VoiceChannelCandidate> = [];
	const channelById = new Map<string, TextChannelCandidate | VoiceChannelCandidate>();

	const currentUserId = UserStore.getCurrentUser()?.id ?? null;

	for (const channel of ChannelStore.allChannels) {
		switch (channel.type) {
			case ChannelTypes.DM:
			case ChannelTypes.DM_PERSONAL_NOTES: {
				const recipientId =
					channel.recipientIds.find((recipientId) => recipientId !== currentUserId) ?? channel.recipientIds.at(0);
				if (!recipientId) break;
				const user = UserStore.getUser(recipientId);
				if (!user) break;

				const title = ChannelUtils.getDMDisplayName(channel);
				const subtitle = user.tag;
				const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);
				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);

				const existing = userCandidates.get(user.id);
				const candidate: UserCandidate = {
					type: QuickSwitcherResultTypes.USER,
					id: user.id,
					title,
					subtitle,
					user,
					dmChannelId: channel.id,
					searchValues,
					sortWeight,
				};

				if (!existing || existing.sortWeight < candidate.sortWeight) {
					userCandidates.set(user.id, candidate);
				} else if (existing.dmChannelId == null) {
					userCandidates.set(user.id, {
						...existing,
						dmChannelId: channel.id,
						sortWeight: Math.max(existing.sortWeight, sortWeight),
					});
				}

				const resolvedCandidate = userCandidates.get(user.id);
				if (resolvedCandidate) {
					userByChannelId.set(channel.id, resolvedCandidate);
				}
				break;
			}
			case ChannelTypes.GROUP_DM: {
				const title = ChannelUtils.getDMDisplayName(channel);
				const participantNames = channel.recipientIds
					.map((recipientId) => {
						const user = UserStore.getUser(recipientId);
						return user ? NicknameUtils.getNickname(user) : null;
					})
					.filter(Boolean) as Array<string>;
				const subtitle = participantNames.length > 0 ? participantNames.join(', ') : i18n._(msg`Group message`);
				const searchValues = [title, ...participantNames];

				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);

				const candidate: GroupDMCandidate = {
					type: QuickSwitcherResultTypes.GROUP_DM,
					id: channel.id,
					title,
					subtitle,
					channel,
					searchValues,
					sortWeight,
				};

				groupDMCandidates.push(candidate);
				groupDMByChannelId.set(channel.id, candidate);
				break;
			}
			case ChannelTypes.GUILD_TEXT: {
				if (!channel.guildId) break;
				const guild = guildMap.get(channel.guildId) ?? null;
				const title = channel.name ? channel.name : i18n._(msg`Unknown channel`);
				const subtitle = guild?.name;
				const searchValues = [
					channel.name ?? '',
					channel.topic ?? '',
					guild?.name ?? '',
					channel.parentId ?? '',
				].filter(Boolean);

				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);

				const candidate: TextChannelCandidate = {
					type: QuickSwitcherResultTypes.TEXT_CHANNEL,
					id: channel.id,
					title,
					subtitle,
					channel,
					guild,
					searchValues,
					sortWeight,
				};

				textChannelCandidates.push(candidate);
				channelById.set(channel.id, candidate);
				break;
			}
			case ChannelTypes.GUILD_VOICE: {
				if (!channel.guildId) break;
				const guild = guildMap.get(channel.guildId) ?? null;
				const title = channel.name ?? i18n._(msg`Voice channel`);
				const subtitle = guild?.name;
				const searchValues = [channel.name ?? '', guild?.name ?? ''].filter(Boolean);

				const baseWeight = getChannelRecency(channel);
				const sortWeight = getChannelSortWeight(channel.id, baseWeight);

				const candidate: VoiceChannelCandidate = {
					type: QuickSwitcherResultTypes.VOICE_CHANNEL,
					id: channel.id,
					title,
					subtitle,
					channel,
					guild,
					searchValues,
					sortWeight,
				};

				voiceChannelCandidates.push(candidate);
				channelById.set(channel.id, candidate);
				break;
			}
			default:
				break;
		}
	}

	for (const relationship of RelationshipStore.getRelationships()) {
		if (relationship.type !== RelationshipTypes.FRIEND) {
			continue;
		}

		const user = relationship.user;
		if (!userCandidates.has(user.id)) {
			const title = NicknameUtils.getNickname(user);
			const subtitle = user.tag;
			const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);

			userCandidates.set(user.id, {
				type: QuickSwitcherResultTypes.USER,
				id: user.id,
				title,
				subtitle,
				user,
				dmChannelId: null,
				searchValues,
				sortWeight: relationship.since.getTime(),
			});
		}
	}

	for (const user of UserStore.getUsers()) {
		if (user.id === currentUserId) continue;
		if (!userCandidates.has(user.id)) {
			const title = NicknameUtils.getNickname(user);
			const subtitle = user.tag;
			const searchValues = [title, subtitle, user.username, user.id].filter(Boolean);

			userCandidates.set(user.id, {
				type: QuickSwitcherResultTypes.USER,
				id: user.id,
				title,
				subtitle,
				user,
				dmChannelId: null,
				searchValues,
				sortWeight: SnowflakeUtils.extractTimestamp(user.id),
			});
		}
	}

	const selectedGuildId = SelectedGuildStore.selectedGuildId;
	if (selectedGuildId) {
		const guildMembers = GuildMemberStore.getMembers(selectedGuildId);
		for (const member of guildMembers) {
			if (member.user.id === currentUserId) continue;
			if (!userCandidates.has(member.user.id)) {
				const title = member.nick ?? NicknameUtils.getNickname(member.user);
				const subtitle = member.user.tag;
				const searchValues = [title, subtitle, member.user.username, member.user.id, member.nick].filter(
					Boolean,
				) as Array<string>;

				userCandidates.set(member.user.id, {
					type: QuickSwitcherResultTypes.USER,
					id: member.user.id,
					title,
					subtitle,
					user: member.user,
					dmChannelId: null,
					searchValues,
					sortWeight: member.joinedAt ? new Date(member.joinedAt).getTime() : 0,
				});
			}
		}
	}

	const guildCandidates: Array<GuildCandidate> = guilds.map((guild) => ({
		type: QuickSwitcherResultTypes.GUILD,
		id: guild.id,
		title: guild.name,
		subtitle: undefined,
		guild,
		searchValues: [guild.name, guild.vanityURLCode ?? '', guild.id].filter(Boolean),
		sortWeight: guild.joinedAt ? new Date(guild.joinedAt).getTime() : 0,
	}));

	const virtualGuildCandidates: Array<VirtualGuildCandidate> = [];

	virtualGuildCandidates.push({
		type: QuickSwitcherResultTypes.VIRTUAL_GUILD,
		id: 'home',
		title: i18n._(msg`Home`),
		subtitle: i18n._(msg`Direct Messages`),
		virtualGuildType: 'home',
		searchValues: ['Home', 'DM', 'DMs', 'Direct Messages', 'Messages'],
		sortWeight: Date.now(),
	});

	if (FavoritesStore.hasAnyFavorites) {
		virtualGuildCandidates.push({
			type: QuickSwitcherResultTypes.VIRTUAL_GUILD,
			id: 'favorites',
			title: i18n._(msg`Favorites`),
			subtitle: undefined,
			virtualGuildType: 'favorites',
			searchValues: ['Favorites', 'Fav', 'Starred', FAVORITES_GUILD_ID],
			sortWeight: Date.now(),
		});
	}

	const settingsCandidates: Array<SettingsCandidate> = [];

	const hasExpressionPackAccess = UserStore.getCurrentUser()?.isStaff() ?? false;

	const accessibleTabs = getSettingsTabs(i18n).filter((tab) => {
		if (!DeveloperModeStore.isDeveloper && tab.category === 'staff_only') {
			return false;
		}
		if (!hasExpressionPackAccess && tab.type === 'expression_packs') {
			return false;
		}
		return true;
	});

	for (const tab of accessibleTabs) {
		settingsCandidates.push({
			type: QuickSwitcherResultTypes.SETTINGS,
			id: tab.type,
			title: tab.label,
			subtitle: undefined,
			settingsTab: tab,
			settingsSubtab: undefined,
			searchValues: [tab.label, 'settings', 'preferences', tab.type],
			sortWeight: 0,
		});
	}

	for (const subtab of getSettingsSubtabs(i18n)) {
		const parentTab = accessibleTabs.find((t) => t.type === subtab.parentTab);
		if (!parentTab) continue;

		settingsCandidates.push({
			type: QuickSwitcherResultTypes.SETTINGS,
			id: `${subtab.parentTab}_${subtab.type}`,
			title: subtab.label,
			subtitle: parentTab.label,
			settingsTab: parentTab,
			settingsSubtab: subtab,
			searchValues: [subtab.label, parentTab.label, 'settings', subtab.type],
			sortWeight: 0,
		});
	}

	const quickActionCandidates: Array<QuickActionCandidate> = [];

	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_theme',
		title: i18n._(msg`Toggle Theme`),
		subtitle: i18n._(msg`Switch between Light and Dark mode`),
		action: () => {
			const currentTheme = ThemeStore.effectiveTheme;
			const newTheme = currentTheme === ThemeTypes.DARK ? ThemeTypes.LIGHT : ThemeTypes.DARK;
			ThemePreferenceActionCreators.updateThemePreference(newTheme);
		},
		searchValues: ['theme', 'light', 'dark', 'mode', 'switch', 'toggle'],
		sortWeight: 0,
	});

	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_compact_mode',
		title: i18n._(msg`Toggle Compact Mode`),
		subtitle: UserSettingsStore.getMessageDisplayCompact()
			? i18n._(msg`Disable Compact Mode`)
			: i18n._(msg`Enable Compact Mode`),
		action: () => {
			UserSettingsStore.saveSettings({
				messageDisplayCompact: !UserSettingsStore.getMessageDisplayCompact(),
			});
		},
		searchValues: ['compact', 'mode', 'display', 'message', 'toggle'],
		sortWeight: 0,
	});

	quickActionCandidates.push({
		type: QuickSwitcherResultTypes.QUICK_ACTION,
		id: 'toggle_reduced_motion',
		title: i18n._(msg`Toggle Reduced Motion`),
		subtitle: AccessibilityStore.useReducedMotion
			? i18n._(msg`Disable Reduced Motion`)
			: i18n._(msg`Enable Reduced Motion`),
		action: () => {
			AccessibilityStore.updateSettings({
				reducedMotionOverride: !AccessibilityStore.useReducedMotion,
				syncReducedMotionWithSystem: false,
			});
		},
		searchValues: ['reduced', 'motion', 'animation', 'toggle'],
		sortWeight: 0,
	});

	return {
		users: Array.from(userCandidates.values()),
		userByChannelId,
		groupDMs: groupDMCandidates,
		groupDMByChannelId,
		textChannels: textChannelCandidates,
		voiceChannels: voiceChannelCandidates,
		guilds: guildCandidates,
		virtualGuilds: virtualGuildCandidates,
		settings: settingsCandidates,
		quickActions: quickActionCandidates,
		channelById,
	};
}
